import { HttpContext, HttpRequest } from "@angular/common/http";

/**
 * consts
 */
import {
    BYPASS_SESSION_ABORT_CONTEXT,
    SESSION_ABORT_GLOBAL_CONTEXT,
    SESSION_ABORT_SCOPE_CONTEXT,
    SESSION_ABORT_MATCHER_CONTEXT,

} from "../consts";

/**
 * Session Orchestrator Utilities
 * @class SessionOrchestratorUtils
 * @description Provides utility methods for session orchestrator.
 * Contains static methods for checking session orchestrator context.
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export abstract class SessionOrchestratorUtils {

    /**
     * Checks if an HTTP request has the bypass abort context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has bypass abort context set
     * @description Determines if the request should bypass abort.
     * Returns true if the request contains the bypass abort context, indicating
     * that the request should bypass abort for this request.
     */
    public static isHasBypassAbort(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_SESSION_ABORT_CONTEXT) === true;
    }

    /**
     * Checks if an HTTP request has the global abort context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has global abort context set
     * @description Determines if the request should abort the global.
     * Returns true if the request contains the global abort context, indicating
     * that the global session should be aborted for this request.
     */
    public static isGlobalAbort(req: HttpRequest<unknown>): boolean {
        return req.context.get(SESSION_ABORT_GLOBAL_CONTEXT) === true;
    }

    /**
     * Gets the scope abort from the request context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {string | null} The scope abort or null if not set
     * @description Gets the scope abort from the request context.
     * Returns the scope abort if the request contains the scope abort context, otherwise null.
     */
    public static getScopeAbort(req: HttpRequest<unknown>): string | null {
        return req.context.get(SESSION_ABORT_SCOPE_CONTEXT);
    }

    /**
     * Gets the matcher abort from the request context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {((req: HttpRequest<unknown>) => boolean) | null} The matcher abort or null if not set
     * @description Gets the matcher abort from the request context.
     * Returns the matcher abort if the request contains the matcher abort context, otherwise null.
     */
    public static getMatcherAbort(req: HttpRequest<unknown>): ((req: HttpRequest<unknown>) => boolean) | null {
        return req.context.get(SESSION_ABORT_MATCHER_CONTEXT) ?? null;
    }

    /**
     * 
     * @param ctx - The HTTP context to set the bypass abort context on
     * @returns {HttpContext} The HTTP context with the bypass abort context set
     * @description Sets the bypass abort context on the HTTP context.
     * Returns the HTTP context with the bypass abort context set.
     */
    public static withBypass(ctx = new HttpContext()): HttpContext {
        return ctx.set(BYPASS_SESSION_ABORT_CONTEXT, true);
    }

    /**
     * 
     * @param ctx - The HTTP context to set the global abort context on
     * @returns {HttpContext} The HTTP context with the global abort context set
     * @description Sets the global abort context on the HTTP context.
     * Returns the HTTP context with the global abort context set.
     */
    public static withGlobal(ctx = new HttpContext()): HttpContext {
        return ctx.set(SESSION_ABORT_GLOBAL_CONTEXT, true);
    }

    /**
     * 
     * @param scope 
     * @param ctx - The HTTP context to set the scope abort context on
     * @returns {HttpContext} The HTTP context with the scope abort context set
     * @description Sets the scope abort context on the HTTP context.
     * Returns the HTTP context with the scope abort context set.
     */
    public static withScope(scope: string, ctx = new HttpContext()): HttpContext {
        return ctx.set(SESSION_ABORT_SCOPE_CONTEXT, scope);
    }

    /**
     * 
     * @param m - The matcher abort to set on the HTTP context
     * @param ctx - The HTTP context to set the matcher abort context on
     * @returns {HttpContext} The HTTP context with the matcher abort context set
     * @description Sets the matcher abort context on the HTTP context.
     * Returns the HTTP context with the matcher abort context set.
     */
    public static withMatcher(m: ((req: HttpRequest<unknown>) => boolean), ctx = new HttpContext()): HttpContext {
        return ctx.set(SESSION_ABORT_MATCHER_CONTEXT, m);
    }

}
